import apiService from '../services/ApiService';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

type Snapshot = { selected: boolean; count: number; pending: boolean };
type Entry = { snapshot: Snapshot; listeners: Set<() => void>; changed: boolean; hydrated: boolean };
const entries = new Map<string, Entry>();

const awaiting = new Map<Entry, { vehicleId: number; kind: 'like' | 'favorite' }>();
let batchTimer: ReturnType<typeof setTimeout> | undefined;
function hydrate(entry: Entry, vehicleId: number, kind: 'like' | 'favorite') {
  if (entry.hydrated || awaiting.has(entry)) return;
  awaiting.set(entry, { vehicleId, kind });
  if (batchTimer) return;
  batchTimer = setTimeout(async () => {
    batchTimer = undefined;
    const batch = Array.from(awaiting).filter(([item]) => item.listeners.size);
    awaiting.clear();
    if (!batch.length) return;
    const ids = Array.from(new Set(batch.map(([, value]) => value.vehicleId)));
    try {
      for (let offset = 0; offset < ids.length; offset += 200) {
        const response = await apiService.makeRequest<Record<string, { like_count: number; favorite_count: number; user_has_liked: boolean; is_favorited: boolean }>>(`/vehicle_engagement.php?ids=${ids.slice(offset, offset + 200).join(',')}`, { method: 'GET' });
        if (!response.success || !response.data) continue;
        for (const [item, value] of batch) {
          const state = response.data[value.vehicleId];
          if (!state || item.changed || item.snapshot.pending) continue;
          item.hydrated = true;
          item.snapshot = { selected: value.kind === 'like' ? state.user_has_liked : state.is_favorited, count: value.kind === 'like' ? state.like_count : state.favorite_count, pending: false };
          item.listeners.forEach(listener => listener());
        }
      }
    } catch { /* Keep the server-provided initial state and allow a later mount to retry. */ }
  }, 20);
}

/** One in-flight action and one snapshot per account/build/control, including both card faces. */
export function useBuildEngagement(
  account: string,
  vehicleId: number,
  kind: 'like' | 'favorite',
  selected: boolean,
  count: number
) {
  const key = `${account}:${vehicleId}:${kind}`;
  const entry = useMemo(() => {
    const existing = entries.get(key);
    if (existing) return existing;
    const created: Entry = {
      snapshot: {
        selected: Boolean(selected),
        count: Math.max(0, Number(count) || 0),
        pending: false,
      },
      listeners: new Set(),
      changed: false,
      hydrated: false,
    };
    entries.set(key, created);
    return created;
    // Incoming props are reconciled below, without replacing an in-flight shared entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const publish = useCallback(
    (snapshot: Snapshot) => {
      entry.snapshot = snapshot;
      entry.listeners.forEach((listener) => listener());
    },
    [entry]
  );
  const subscribe = useCallback(
    (listener: () => void) => {
      // StrictMode may unsubscribe then resubscribe the same entry.
      entries.set(key, entry);
      entry.listeners.add(listener);
      return () => {
        entry.listeners.delete(listener);
        if (!entry.listeners.size && !entry.snapshot.pending && entries.get(key) === entry)
          entries.delete(key);
      };
    },
    [entry, key]
  );
  const snapshot = useSyncExternalStore(
    subscribe,
    () => entry.snapshot,
    () => entry.snapshot
  );
  useEffect(() => {
    if (!entry.changed && !entry.hydrated && !entry.snapshot.pending) {
      const next = {
        selected: Boolean(selected),
        count: Math.max(0, Number(count) || 0),
        pending: false,
      };
      if (next.selected !== entry.snapshot.selected || next.count !== entry.snapshot.count)
        publish(next);
    }
  }, [entry, selected, count, publish]);
  useEffect(() => { if (vehicleId > 0) hydrate(entry, vehicleId, kind); }, [entry, vehicleId, kind]);
  const toggle = async (
    request: (next: boolean) => Promise<{ selected: boolean; count: number }>
  ) => {
    if (entry.snapshot.pending) return null;
    const before = entry.snapshot;
    const next = !before.selected;
    entry.changed = true;
    publish({ selected: next, count: Math.max(0, before.count + (next ? 1 : -1)), pending: true });
    try {
      const result = await request(next);
      publish({ selected: result.selected, count: Math.max(0, result.count), pending: false });
      return result;
    } catch (error) {
      publish({ ...before, pending: false });
      throw error;
    } finally {
      if (!entry.listeners.size && entries.get(key) === entry) entries.delete(key);
    }
  };
  return { ...snapshot, toggle };
}
